import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PageHeader } from "../components/ui";

const Section = ({ title, children }) => (
  <div>
    <h2 className="mb-4 text-2xl font-semibold text-ink">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);

export default function PrivacyPolicyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Chiparama</title>
        <meta
          name="description"
          content="Chiparama's Privacy Policy, covering data collection, SMS/text messaging consent, cookies, and your privacy rights under US and Canadian law."
        />
        <link rel="canonical" href="https://chiparama.com/privacy-policy" />
      </Helmet>

      <PageHeader eyebrow="Legal" title="Privacy Policy">
        Last updated: August 10, 2026
      </PageHeader>

      <section className="section bg-white">
        <div className="container-x max-w-3xl">
        <div className="space-y-12 leading-relaxed text-slate-600">
          <Section title="Overview">
            <p>
              Chiparama Inc ("Chiparama," "we," "us," or "our") respects your privacy and is committed to protecting the
              personal information you share with us. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you visit chiparama.com or communicate with
              us, including via our Contact Us form, email, or SMS/text messaging. It also describes the
              rights available to you under applicable United States and Canadian privacy laws.
            </p>
            <p>
              By using our website or providing us with your information, you agree to the collection and
              use of information in accordance with this policy.
            </p>
          </Section>

          <Section title="Information We Collect">
            <p>We collect information in the following ways:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                <strong className="text-ink">Information you provide directly</strong> — such as your
                name, email address, phone number, subject, and message content when you submit our
                Contact Us form, along with your SMS opt-in preference.
              </li>
              <li>
                <strong className="text-ink">Automatically collected information</strong> — such as your
                IP address, browser type, device type, and pages visited, which may be collected through
                standard web server logs.
              </li>
            </ul>
            <p>
              We do not currently use advertising or analytics cookies, and we do not knowingly sell your
              personal information to third parties. See "Cookies & Tracking Technologies" below for
              details.
            </p>
          </Section>

          <Section title="How We Use Your Information">
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>To respond to your inquiries and provide the services you request;</li>
              <li>To send job opening alerts and company updates via SMS, where you have opted in;</li>
              <li>To communicate with you by email regarding your inquiry or our services;</li>
              <li>To maintain the security and proper functioning of our website; and</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </Section>

          <Section title="Cookies & Tracking Technologies">
            <p>
              Our website may use strictly necessary cookies required for basic site functionality (for
              example, remembering your session while browsing). We do not currently deploy advertising,
              cross-site tracking, or analytics cookies. If this changes in the future, we will update this
              policy and, where required by law, present a cookie consent option and honor recognized
              opt-out signals such as the Global Privacy Control (GPC).
            </p>
          </Section>

          <Section title="SMS/Text Messaging Consent">
            <p>
              ✅ By opting in for text messages, you agree to receive job opening alerts and company
              updates from Chiparama Inc at the number provided. Message frequency varies. Message and data
              rates may apply. Consent to receive SMS messages is not a condition of purchasing any goods
              or services.
            </p>
            <p>
              We will not share your opt-in to an SMS campaign with any third party for purposes unrelated
              to providing you with the services of that campaign. We may share your personal data,
              including your SMS opt-in or consent status, with third parties that help us provide our
              messaging services, including but not limited to platform providers, phone companies, and
              other vendors who assist us in the delivery of text messages.
            </p>
            <p>
              All of the above categories exclude text messaging originator opt-in data and consent; this
              information will not be shared with any third parties for marketing purposes.
            </p>
          </Section>

          <Section title="Opt-Out Process">
            <p>You may choose to stop receiving text messages from Chiparama Inc at any time.</p>
            <p>
              To opt out, simply reply <strong className="text-ink">STOP</strong> or{" "}
              <strong className="text-ink">UNSUBSCRIBE</strong> to any text message you receive from us.
              Once we receive your opt-out request, you will be removed from our messaging list — no later
              than 10 business days, though typically immediately — and will no longer receive SMS messages
              from us, aside from a one-time confirmation of your opt-out.
            </p>
            <p>
              For assistance, reply <strong className="text-ink">HELP</strong> or contact us at{" "}
              <a href="mailto:contact@chiparama.com" className="link">
                contact@chiparama.com
              </a>
              .
            </p>
          </Section>

          <Section title="Email Communications">
            <p>
              Any email communications we send comply with the CAN-SPAM Act: we will not use false or
              misleading header information or deceptive subject lines, we will identify promotional
              messages as such, and every email will include our contact information and a working
              opt-out/unsubscribe mechanism. Opt-out requests are honored promptly and within the time
              required by law.
            </p>
          </Section>

          <Section title="Your Privacy Rights — United States">
            <p>
              Depending on your state of residence, you may have rights under state privacy laws, including
              the California Consumer Privacy Act as amended by the California Privacy Rights Act
              (CCPA/CPRA), and comparable laws in states such as Virginia, Colorado, Connecticut, Utah,
              Texas, Indiana, Kentucky, and Rhode Island. These rights may include the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Know what personal information we collect, use, and disclose about you;</li>
              <li>Request access to or a copy of your personal information;</li>
              <li>Request correction of inaccurate personal information;</li>
              <li>Request deletion of your personal information;</li>
              <li>Opt out of the sale or sharing of personal information and targeted advertising; and</li>
              <li>Not be discriminated against for exercising any of these rights.</li>
            </ul>
            <p>
              <strong className="text-ink">We do not sell or share your personal information</strong> for
              cross-context behavioral advertising. Where required, we honor opt-out preference signals such
              as the Global Privacy Control (GPC). To exercise any of these rights, please{" "}
              <Link to="/contact" className="link">
                contact us
              </Link>
              .
            </p>
          </Section>

          <Section title="Your Privacy Rights — Canada">
            <p>
              For visitors in Canada, we handle personal information in accordance with the Personal
              Information Protection and Electronic Documents Act (PIPEDA) and, where applicable,
              provincial privacy legislation. In line with PIPEDA's fair information principles, we:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Identify the purposes for collecting your information before or at the time of collection;</li>
              <li>Collect only the information reasonably necessary for those purposes;</li>
              <li>Obtain meaningful consent before collecting, using, or disclosing your information;</li>
              <li>Limit use, disclosure, and retention of your information to those purposes;</li>
              <li>Keep your information as accurate, complete, and up to date as necessary; and</li>
              <li>Provide you access to your personal information and the ability to challenge its accuracy.</li>
            </ul>
            <p>
              Any commercial electronic messages (including SMS) we send to Canadian recipients comply with
              Canada's Anti-Spam Legislation (CASL): we obtain your consent before sending, clearly identify
              ourselves as the sender, and provide a working unsubscribe mechanism (reply STOP) in every
              message.
            </p>
          </Section>

          <Section title="Children's Privacy">
            <p>
              Our website and services are not directed to children under 13, and we do not knowingly
              collect personal information from children under 13. If you believe a child has provided us
              with personal information, please contact us and we will promptly delete it.
            </p>
          </Section>

          <Section title="Data Retention & Security">
            <p>
              We retain personal information only as long as necessary to fulfill the purposes described in
              this policy, respond to your inquiry, or comply with legal obligations. We use reasonable
              administrative and technical safeguards to protect your information, though no method of
              transmission or storage is completely secure.
            </p>
          </Section>

          <Section title="International Users">
            <p>
              Our website is operated from the United States. If you access our website from outside the
              United States, including from Canada, your information may be transferred to, stored, and
              processed in the United States, where privacy laws may differ from those of your jurisdiction.
            </p>
          </Section>

          <Section title="Third-Party Links">
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy
              practices or content of those sites and encourage you to review their privacy policies.
            </p>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page
              with an updated "Last updated" date. Your continued use of our website after changes are
              posted constitutes acceptance of the revised policy.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              If you have questions about this Privacy Policy or would like to exercise your privacy
              rights, please{" "}
              <Link to="/contact" className="link">
                contact us
              </Link>{" "}
              or email{" "}
              <a href="mailto:contact@chiparama.com" className="link">
                contact@chiparama.com
              </a>
              . See also our{" "}
              <Link to="/terms-and-conditions" className="link">
                Terms & Conditions
              </Link>
              .
            </p>
          </Section>
        </div>
        </div>
      </section>
    </>
  );
}
